import { Component, OnInit, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { Input } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { IonItemSliding } from '@ionic/angular';

import { Subscription } from 'rxjs';

import { PriceInfo, PriceType } from '@unpispas/upp-defs';
import { ViewRenderer } from '@unpispas/upp-wdgt';
import { TicketPayment } from '@unpispas/upp-data';
import { UppPinpadSessionComponent } from '@unpispas/feature/pinpad';

import { clockService } from '@unpispas/upp-base';
import { languageService, languageSupport, toastService } from '@unpispas/upp-base';
import { dataService } from '@unpispas/upp-data';
import { viewService } from '@unpispas/upp-base';

import { PlaceView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';

import { queueService } from '../../services/queue.service';

/****************************/
/* TICKETPRODUCT DISPLAY    */
/****************************/

export class TicketDisplay {
    constructor(private lang: languageService, private ticket: TicketView, private change: ViewRenderer, private data: dataService){
        // nothing to do
    }

    markForCheck(): void {
        this._elapsedtime = null;
        this.change.markForCheck();
    }    

    get toprepare(): boolean{
        return this.ticket.IsOpened && !this.ticket.IsReady;
    }

    get topayment(): boolean{
        return !this.ticket.IsPaid && (this.ticket.IsOpened || this.ticket.CanCash || this.ticket.CanCard);
    }

    get cancash(): boolean {
        return this.ticket.CanCash;
    }

    get cancard(): boolean {
        return this.ticket.CanCard;
    }

    /** Unconfirmed card-terminal payment (`PinpadPending`) — warning badge on the thumb. */
    get cardpending(): boolean {
        return !!this.ticket.PinpadPending;
    }

    private _elapsedtime: string | null = null;
    private get _elapsed(): string | null {
        const updated = this.ticket.updated;
        if (!updated){
            return null;
        }

        const nowdate = new Date();

        if (!this.ticket.IsRecent){
            const _dd = updated.getDate().toString();
            const _mm = (updated.getMonth()+1).toString();
            const _yy = updated.getFullYear().toString();

            const _HH = (updated.getHours().toString().padStart(2, '0'));
            const _MM = (updated.getMinutes().toString().padStart(2, '0'));

            return _dd + "/" + _mm + "/" + _yy + " " + _HH + ":" + _MM;
        }
        else {
            const _minutes = Math.round((nowdate.getTime() - updated.getTime()) / 60000);
            if (_minutes < 1){
                return this.lang.tr('@elapsed_time_just_now');
            }

            if (_minutes < 60){
                return this.lang.tr('@elapsed_time_minutes', [ _minutes ]);
            }

            const _hh = Math.floor(_minutes / 60);
            const _mm = Math.floor(_minutes % 60);
    
            return this.lang.tr('@elapsed_time_hours', [ _hh, _mm]);                
        }
    }

    get elapsed(): string | null {
        if (this._elapsedtime === null){
            this._elapsedtime = this._elapsed;
        }
        return this._elapsedtime;
    }

    /**
     * Short order label for the thumb (`PREFIX-N` → `N`).
     * Returns `null` while the server has not assigned `orderno` yet so the
     * template can show the waiting spinner (same contract as v3 `OrderNo`).
     */
    get orderno(): string | null {
        const raw = this.ticket.orderno;
        if (raw){
            const dash = raw.indexOf('-');
            if (dash !== -1){
                return raw.substring(dash + 1);
            }

            return raw;
        }
        
        return null;
    }

    get tablealias(): string | null {
        /* TODO: pasa por aqui - poder establecer un alias para la mesa */
        return null;    // TODO
    }

    get tableno(): string | null {
        const qrcode = this.ticket.qrcode;
        if (qrcode && qrcode.number){
            return qrcode.number.toString();
        }
        return null;
    }

    /**
     * List thumbs show the persisted ticket total. Full `priceinfo` (offers /
     * extras / discounts) is reserved for edit/cart where the live cart can
     * diverge from the stored total.
     */
    get price(): PriceType | null {
        const _total = this.ticket.price;
        if (typeof _total === 'number'){
            return new PriceInfo(_total).price;
        }
        return this.ticket.priceinfo;
    }

    get ispaid(): boolean {
        return this.ticket.IsPaid;
    }

    get payment(): string | null {
        return this.ticket.payment;
    }

    private _PayAccountName(): string | null {
        const account = this.ticket.account;
        if (!account){
            return null;
        }

        if (account.name){
            return account.name.trim().toUpperCase();
        }

        if (account.business){
            return account.business.trim().toUpperCase();
        }

        return null;
    }

    get paidon(): string | null {
        const _paidon = this.ticket.paidon;
        if (_paidon){
            const _dd = _paidon.getDate().toString();
            const _mm = (_paidon.getMonth()+1).toString();
            const _yy = _paidon.getFullYear().toString();

            const _HH = (_paidon.getHours().toString().padStart(2, '0'));
            const _MM = (_paidon.getMinutes().toString().padStart(2, '0'));

            return _dd + "/" + _mm + "/" + _yy + " " + _HH + ":" + _MM;
        }

        // if paid to account then display the account name
        if (this.ticket.payment == 'PAYACCOUNT'){
            return this._PayAccountName();
        }

        return null;
    }

    get isguest(): boolean{
        return this.ticket.source === 'QR';
    }

    get waiter(): boolean {
        return !!this.ticket.request;
    }

    get cancelled(): boolean {
        return this.ticket.IsCancel;
    }

    get invoice(): boolean {
        return this.ticket.ToInvoice;
    }
}

/****************************/
/* UPP-PRODUCT-TICKET       */
/****************************/

@Component({
    selector: 'upp-ticket-thumb',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-thumb.html',
    styleUrls: [
      './upp-thumb.scss'
    ],
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef]
    }]       
})
export class UppTicketthumbComponent extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input() ticket: TicketView | null = null;

    @ViewChild(UppPinpadSessionComponent) pinpadSession: UppPinpadSessionComponent | null = null;

    private _ticket_subscription: Subscription | null = null;
    private _place_subscription:  Subscription | null = null;
    private _minute_subscription: Subscription | null = null;

    get place(): PlaceView | null {
        if (this.ticket){
            return this.ticket.place;
        }

        return null;
    }

    constructor(
        private lang: languageService,
        private change: ViewRenderer,
        private clock: clockService,
        private data: dataService,
        public view: viewService,
        private queue: queueService,
        private toast: toastService,
    ){
        super(lang, change.cdref);
    }    

    ngOnInit(){
        this._minute_subscription = this.clock.OnOneMinuteTick.subscribe(
        () => {
            if (this.display){
                this.display.markForCheck();
            }
        });
    }

    ngOnChanges(){
        if (this._ticket_subscription){
            this._ticket_subscription.unsubscribe();
            this._ticket_subscription = null;
        }

        if (this._place_subscription){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        if (this.ticket){
            this._ticket_subscription = this.ticket.OnViewRefresh.subscribe(
            () => {
                if (this.display){
                    this.display.markForCheck();
                }
            });
        }


        if (this.place){
            this._place_subscription = this.place.OnViewRefresh.subscribe(
            () => {
                if (this.display){
                    this.display.markForCheck();
                }
            });    
        }

        this._Enqueue();
    }

    ngOnDestroy(): void {
        super.OnDestroy();

        if (this._slidingtimer !== null){
            clearTimeout(this._slidingtimer);
            this._slidingtimer = null;
        }

        if (this._minute_subscription){
            this._minute_subscription.unsubscribe();
            this._minute_subscription = null;
        }

        if (this._place_subscription){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        if (this._ticket_subscription){
            this._ticket_subscription.unsubscribe();
            this._ticket_subscription = null;
        }

        this._Dequeue();
    }
    
    /********************************/
    /* DISPLAY OPTIONS              */
    /********************************/    
    
    private _display: TicketDisplay | null = null; 
    get display() : TicketDisplay | null {
        if (this.ticket == null){
            return null;
        }
        
        if (this._display === null){
            this._display = new TicketDisplay(this.lang, this.ticket, this.change, this.data);
        }

        return this._display;
    }   
    
    /***********************************/
    /* EVENT HANDLERS                  */
    /***********************************/

    private _onaction = false;
    get IsSliding(){
        return this._onaction;
    }
    private _sliding = false;
    private _slidingtimer: ReturnType<typeof setTimeout> | null = null;

    OnSlidingStart(): void {
        this._sliding = true;
        if (this._slidingtimer !== null){
            clearTimeout(this._slidingtimer);
        }

        this._slidingtimer = setTimeout(() => {
            this._slidingtimer = null;
            this._sliding = false;
        }, 250);
    }

    OnSlidingDrag(event: CustomEvent<{ amount?: number; ratio?: number }>): void {
        this.OnSlidingStart();

        const amount = event.detail?.amount ?? 0;
        this._onaction = Math.abs(amount) > 10;
    }

    OnSlidingClick(event: Event, slider: IonItemSliding): void {
        if (this._sliding){
            event.stopPropagation();
            return;
        }

        if (this._onaction){
            event.stopPropagation();

            const closer = slider.close?.();
            const settled = closer !== undefined && closer !== null && typeof (closer as Promise<void>).finally === 'function'
                ? (closer as Promise<void>)
                : Promise.resolve();

            void settled.finally(() => {
                this._onaction = false;
                this.change.markForCheck();
            });
        }
    }

    private _print_pressed_timeout: ReturnType<typeof setTimeout> | null = null;
    private _print_ignore_click = false;

    OnPrintPressed(pressed: boolean): void {
        if (pressed){
            if (this._print_pressed_timeout !== null){
                clearTimeout(this._print_pressed_timeout);
            }

            this._print_pressed_timeout = setTimeout(() => {
                this._print_pressed_timeout = null;
                this._print_ignore_click = true;

                // TODO(v3): on long press (~1s), open printer selection modal
                // when the environment supports explicit multi-POS device selection.
            }, 1000);
            return;
        }

        if (this._print_pressed_timeout !== null){
            clearTimeout(this._print_pressed_timeout);
            this._print_pressed_timeout = null;
        }
    }

    OnPrintClicked(event: Event, slider: IonItemSliding): void {
        event.stopPropagation();
        slider.close();

        if (this._print_ignore_click){
            setTimeout(() => {
                this._print_ignore_click = false;
            }, 0);
            return;
        }

        if (this.ticket && this.ticket.CanPrint){
            this.ticket.DoPrint(false);
        }
    }

    PrepareTicket(event: Event){
        event.stopPropagation();

        if (!this.ticket || !this.ticket.place?.AllowTicketCommit){
            return;
        }

        this.ticket.OnReady();
        this.ticket.OnCommit(null);
    }  
    
    async PayTicket(event: Event, payment: TicketPayment){
        if (!this.ticket || this.ticket.ToInvoice){
            return;
        }

        if (!this.ticket.place?.AllowTicketPayment){
            return;
        }

        event.stopPropagation();

        if (await this._DoPayTicket(payment)){
            this.ticket.OnCommit(payment);
        }
    }

    /********************************/
    /* TICKET PAYMENT METHODS       */
    /********************************/

    private _waitmodal = false;

    PayCashTickets: TicketView[] | null = null;
    PayCardTickets: TicketView[] | null = null;

    private _payCashResolve: ((ok: boolean) => void) | null = null;
    private _payCardResolve: ((ok: boolean) => void) | null = null;

    private OnPaycash(): Promise<boolean> {
        if (this._waitmodal || !this.ticket) {
            return Promise.resolve(false);
        }

        this._waitmodal = true;
        this.PayCashTickets = [this.ticket];
        this.change.markForCheck();
        return new Promise((resolve) => {
            this._payCashResolve = resolve;
        });
    }

    OnPayCashAccept(result: { amount: number | null }): void {
        if (this.ticket && result) {
            this.ticket.given = result.amount;
        }
        this._FinishPayCash(result != null);
    }

    OnPayCashClose(): void {
        this._FinishPayCash(false);
    }

    private _FinishPayCash(ok: boolean): void {
        const resolve = this._payCashResolve;
        this._payCashResolve = null;
        this.PayCashTickets = null;

        this._waitmodal = false;
        this.change.markForCheck();
        
        resolve?.(ok);
    }

    private OnPaycard(): Promise<boolean> {
        if (this._waitmodal || !this.ticket) {
            return Promise.resolve(false);
        }

        this._waitmodal = true;
        this.PayCardTickets = [this.ticket];
        this.change.markForCheck();
        return new Promise((resolve) => {
            this._payCardResolve = resolve;
        });
    }

    OnPayCardAccept(): void {
        this._FinishPayCard(true);
    }

    OnPayCardClose(): void {
        this._FinishPayCard(false);
    }

    private _FinishPayCard(ok: boolean): void {
        const resolve = this._payCardResolve;
        this._payCardResolve = null;
        this.PayCardTickets = null;

        this._waitmodal = false;
        this.change.markForCheck();
        
        resolve?.(ok);
    }

    private async OnPinpadPay(): Promise<boolean> {
        if (this._waitmodal || !this.ticket || !this.place || !this.pinpadSession) {
            return false;
        }

        this._waitmodal = true;
        this.change.markForCheck();
        try {
            return await this.pinpadSession.Charge([this.ticket], this.place);
        }
        finally {
            this._waitmodal = false;
            this.change.markForCheck();
        }
    }

     private async _DoPayTicket(payment: TicketPayment): Promise <boolean> {
        if (!this.ticket || !this.place){
            return false;
        }

        let _dopay = true;

        if (payment == 'PAYCASH'){
            if (this.place.CashModal){
                _dopay = await this.OnPaycash();
            }            
        }

        if (payment == 'PAYCARD'){
            switch (this.place.CardMethod){
                case 'UNLINKED':
                    this.toast.ShowAlert('warning', this.lang.tr('@ticket_cardterm_unlinked'));
                    return false;

                case 'TERMINAL':
                    return await this.OnPinpadPay();
                    
                default:
                    if (this.place.CardModal){
                        _dopay = await this.OnPaycard();
                    }
                    break;
            }
        }

        if (_dopay){
            await this.ticket.DoPayment(payment);
        }

        return _dopay;
    }

    /********************************/
    /* TICKET PRINTER QUEUE         */
    /********************************/

    private _queued_ticket: TicketView | null = null;

    private _Enqueue(): void {
        if (this._queued_ticket === this.ticket){
            return;
        }

        this._Dequeue();
        if (this.ticket){
            this.queue.Watch(this.ticket);
            this._queued_ticket = this.ticket;
        }
    }

    private _Dequeue(): void {
        if (!this._queued_ticket){
            return;
        }

        this.queue.Unwatch(this._queued_ticket);
        this._queued_ticket = null;
    }
}
